import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useApplyCoupon, useGetCouponHistory } from '@/service/coupon/hook';
import { DataTable } from '@/components/ui/data-table/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { CouponHistoryItem } from '@/service/coupon';
import { fFullDateTime } from '@/utils/formatTime';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { Badge } from '@/components/ui/badge';
import { useMemo } from 'react';

const formSchema = z.object({
  code: z.string().min(1, 'Coupon code is required'),
});

const columns: ColumnDef<CouponHistoryItem>[] = [
  {
    accessorKey: 'code',
    header: 'Coupon Code',
    size: 150,
    cell: ({ row }) => (
      <span className="font-mono">{row.getValue('code')}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 100,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue('amount'));
      return <span>{fCurrencyUSDExtended(amount / 100)}</span>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      const variant =
        status === 'applied'
          ? 'success'
          : status === 'expired'
            ? 'secondary'
            : 'destructive';
      return <Badge variant={variant}>{status}</Badge>;
    },
  },
  {
    accessorKey: 'applied_at',
    header: 'Applied Date',
    size: 150,
    cell: ({ row }) => {
      const dateStr = row.getValue('applied_at') as string;
      return dateStr ? fFullDateTime(dateStr) : '-';
    },
  },
  {
    accessorKey: 'expired_at',
    header: 'Expiry Date',
    size: 150,
    cell: ({ row }) => {
      const dateStr = row.getValue('expired_at') as string;
      return dateStr ? fFullDateTime(dateStr) : '-';
    },
  },
];

const Coupon: React.FC = () => {
  const { mutateAsync, isPending } = useApplyCoupon();
  const { data: couponHistory, isLoading: isLoadingHistory } =
    useGetCouponHistory();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
    },
  });

  const historyData = useMemo(
    () => couponHistory?.data || [],
    [couponHistory?.data]
  );

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const response = await mutateAsync({ code: data.code });
      toast.success(response.data.message || 'Coupon applied successfully');
      form.reset();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || 'Failed to apply coupon';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="container flex flex-col gap-3">
      <p className="text-xl font-semibold">Apply Coupon</p>
      <div className="flex flex-col gap-8">
        <div>
          <Separator className="mb-6" />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Coupon Code</FormLabel>
                      <FormDescription>
                        Enter your coupon code to redeem credits
                      </FormDescription>
                      <FormControl>
                        <Input
                          className="max-w-sm"
                          placeholder="Enter coupon code"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="self-start"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? 'Applying...' : 'Apply Coupon'}
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <div>
          <Separator className="mb-6" />
          <p className="mb-4 text-lg font-medium">Coupon History</p>
          <DataTable
            columns={columns}
            data={historyData}
            isLoading={isLoadingHistory}
            isHidePagination={historyData.length <= 10}
          />
        </div>
      </div>
    </div>
  );
};

export default Coupon;
